import { createContext, FC, ReactNode, useContext, useEffect, useState } from "react";
import Song from "../models/song";

type LicenseContextType = {
	songsInCart: Song[];
	addSongToCart: (song: Song) => void;
	removeSongFromCart: (songId: string) => void;
	clearCart: () => void;
	isSongInCart: (songId: string) => boolean;
};

type Props = {
	children: ReactNode;
};

export const LICENSE_CART_STORAGE_KEY = 'tkfm_license_cart';

const readCartFromStorage = (): Song[] => {
	try {
		const stored = sessionStorage.getItem(LICENSE_CART_STORAGE_KEY);
		return stored ? JSON.parse(stored) as Song[] : [];
	} catch {
		return [];
	}
};

const LicenseContext = createContext<LicenseContextType | null>(null);

export const LicenseProvider: FC<Props> = ({ children }) => {
	const [songsInCart, setSongsInCart] = useState<Song[]>(readCartFromStorage);

	useEffect(() => {
		sessionStorage.setItem(LICENSE_CART_STORAGE_KEY, JSON.stringify(songsInCart));
	}, [songsInCart]);

	const addSongToCart = (song: Song) => {
		if (songsInCart.some(s => s.id === song.id)) {
			return;
		}

		setSongsInCart(prev => [...prev, song]);
	};

	const removeSongFromCart = (songId: string) => {
		setSongsInCart(prev => prev.filter(s => s.id !== songId));
	};

	const clearCart = () => {
		setSongsInCart([]);
	};

	const isSongInCart = (songId: string) => {
		return songsInCart.some(s => s.id === songId);
	};

	return (
		<LicenseContext.Provider value={{ songsInCart, addSongToCart, removeSongFromCart, clearCart, isSongInCart }}>
			{children}
		</LicenseContext.Provider>
	);
};

export const useLicenses = (): LicenseContextType => {
	const context = useContext(LicenseContext);

	if (!context) {
		throw new Error('useLicenses must be used within a LicenseProvider.');
	}

	return context;
};
